import React, { useRef } from 'react';
import { TOTAL_STORY_TOPICS } from '../../lib/constants';
import { formatCompletionEstimate } from '../../lib/notificationEstimate';

export type NotificationFrequency =
    | 'daily'
    | 'every_2_3_days'
    | 'weekly'
    | 'off';

interface FrequencyPickerProps {
    value: NotificationFrequency;
    onChange: (value: NotificationFrequency) => void;
    /**
     * Override the topic count used for the completion estimate. Defaults to
     * TOTAL_STORY_TOPICS (the master template count). Pass this when an
     * existing story has fewer remaining topics so the estimate reflects
     * what's actually left.
     */
    totalQuestions?: number;
    /**
     * Optional override for the legend.
     */
    legend?: string;
    /**
     * Optional relationship label ("madre", "padre", "abuela"…) interpolated
     * into the off-checkbox recommendation callout. Defaults to a generic
     * phrase when not provided.
     */
    relationship?: string;
    name?: string;
}

interface SegmentOption {
    value: Exclude<NotificationFrequency, 'off'>;
    label: string;
    daysPerQuestion: number;
}

const SEGMENTS: SegmentOption[] = [
    { value: 'daily', label: 'Cada día', daysPerQuestion: 1 },
    { value: 'every_2_3_days', label: 'Cada 2-3 días', daysPerQuestion: 2.5 },
    { value: 'weekly', label: 'Cada semana', daysPerQuestion: 7 },
];

const FrequencyPicker: React.FC<FrequencyPickerProps> = ({
    value,
    onChange,
    totalQuestions = TOTAL_STORY_TOPICS,
    legend = 'Te enviaremos recordatorios según el tiempo en el que quieras completar la historia.',
    relationship,
    name = 'notificationFrequency',
}) => {
    // Remember the last non-off selection so toggling the "no avisos"
    // checkbox off restores the previous cadence rather than always
    // reverting to daily.
    const lastActiveRef = useRef<Exclude<NotificationFrequency, 'off'>>(
        value !== 'off' ? value : 'daily',
    );
    if (value !== 'off') {
        lastActiveRef.current = value;
    }

    const isOff = value === 'off';
    const activeSegment = isOff ? lastActiveRef.current : value;
    const activeOption = SEGMENTS.find((s) => s.value === activeSegment)!;
    const estimate = formatCompletionEstimate({
        daysPerQuestion: activeOption.daysPerQuestion,
        totalQuestions,
    });

    const handleSegment = (next: Exclude<NotificationFrequency, 'off'>) => {
        lastActiveRef.current = next;
        onChange(next);
    };

    const handleToggleOff = (checked: boolean) => {
        onChange(checked ? 'off' : lastActiveRef.current);
    };

    const offHeadline = relationship
        ? `Recomendamos mantener los avisos. Así te aseguras de que la historia de tu ${relationship.toLowerCase()} no se quede a medias.`
        : 'Recomendamos mantener los avisos. Así te aseguras de que la historia no se quede a medias.';

    return (
        <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-stone-700">
                {legend}
            </legend>

            {/* Segmented bar — three equal segments. Disabled-look (lower
                opacity) when "no avisos" is ticked but still tappable so the
                user can pick a cadence before un-ticking. */}
            <div
                role="radiogroup"
                aria-label="Frecuencia de los avisos"
                className={`flex rounded-xl border-2 border-stone-200 overflow-hidden bg-white ${
                    isOff ? 'opacity-50' : ''
                }`}
            >
                {SEGMENTS.map((option, i) => {
                    const selected = !isOff && value === option.value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => handleSegment(option.value)}
                            className={`flex-1 px-3 py-2.5 text-sm transition-colors ${
                                i > 0 ? 'border-l-2 border-stone-200' : ''
                            } ${
                                selected
                                    ? 'bg-rose-500 text-white font-medium'
                                    : 'bg-white text-stone-800 hover:bg-stone-50'
                            }`}
                        >
                            {option.label}
                        </button>
                    );
                })}
                {/* Hidden radio for form name attribution. */}
                <input
                    type="hidden"
                    name={name}
                    value={value}
                    readOnly
                />
            </div>

            {/* Estimate field — hidden when off-checkbox is ticked because
                a paused cadence has no completion timeline. */}
            {!isOff && (
                <p className="text-xs text-stone-500">
                    Tiempo estimado para completar la historia:{' '}
                    <span className="font-medium text-stone-800">{estimate}</span>
                </p>
            )}

            {/* Off-checkbox — single source of truth for "no avisos". Adds
                friction (a checkbox toggle + a callout) compared with
                picking it as a 4th segment. */}
            <label className="flex items-start gap-2 cursor-pointer pt-1">
                <input
                    type="checkbox"
                    checked={isOff}
                    onChange={(e) => handleToggleOff(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-rose-500 shrink-0"
                />
                <span className="text-sm text-stone-700">
                    No quiero recibir avisos
                </span>
            </label>

            {isOff && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm text-amber-900 leading-relaxed">
                        {offHeadline}
                    </p>
                </div>
            )}
        </fieldset>
    );
};

export default FrequencyPicker;
